import React, { useEffect, useRef } from 'react';

interface SimpleParticlesProps {
  count?: number;
}

interface Particle {
  element: HTMLDivElement;
  baseX: number;
  baseY: number;
  currentX: number;
  currentY: number;
}

export const SimpleParticles: React.FC<SimpleParticlesProps> = ({ count = 30 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Create particles
    particlesRef.current = [];
    
    for (let i = 0; i < count; i++) {
      const particle = document.createElement('div');
      const size = Math.random() * 3 + 2;
      const hue = 200 + Math.random() * 60;
      
      const baseX = Math.random() * window.innerWidth;
      const baseY = Math.random() * window.innerHeight;
      
      particle.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        background: hsl(${hue}, 80%, 60%);
        border-radius: 50%;
        left: ${baseX}px;
        top: ${baseY}px;
        pointer-events: none;
        box-shadow: 0 0 ${size * 2}px hsl(${hue}, 80%, 60%);
        transition: all 0.2s ease-out;
        z-index: 1;
      `;
      
      container.appendChild(particle);
      
      particlesRef.current.push({
        element: particle,
        baseX,
        baseY,
        currentX: baseX,
        currentY: baseY
      });
    }

    // Animation loop for particle movement
    const animate = () => {
      particlesRef.current.forEach(particle => {
        particle.element.style.left = `${particle.currentX}px`;
        particle.element.style.top = `${particle.currentY}px`;
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };

    // Mouse movement handler
    const handleMouseMove = (e: MouseEvent) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      
      particlesRef.current.forEach(particle => {
        const dx = mouseX - particle.baseX;
        const dy = mouseY - particle.baseY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 120) {
          const force = (120 - distance) / 120;
          const moveX = dx * force * 0.3;
          const moveY = dy * force * 0.3;
          
          particle.currentX = particle.baseX + moveX;
          particle.currentY = particle.baseY + moveY;
          
          // Enhanced glow effect
          const glowSize = 4 + force * 8;
          const hue = 200 + Math.random() * 60;
          particle.element.style.boxShadow = `0 0 ${glowSize}px hsl(${hue}, 80%, 70%)`;
          particle.element.style.transform = `scale(${1 + force * 0.4})`;
        } else {
          // Return to base position
          particle.currentX = particle.baseX;
          particle.currentY = particle.baseY;
          particle.element.style.boxShadow = `0 0 4px hsl(220, 80%, 60%)`;
          particle.element.style.transform = 'scale(1)';
        }
      });
    };

    // Start animation and add event listener
    animate();
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Cleanup particles
      particlesRef.current.forEach(particle => {
        if (particle.element.parentNode) {
          particle.element.parentNode.removeChild(particle.element);
        }
      });
    };
  }, [count]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 5,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}
    />
  );
};

export default SimpleParticles; 